import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Box, Button, Chip, Divider, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { CheckCircle, LightbulbOutlined, School } from '@mui/icons-material';
import { ModuleContent } from './content/modules';

interface ModuleViewerProps {
  module: ModuleContent | null;
  open: boolean;
  onClose: () => void;
  onComplete: (moduleId: string) => void;
  isCompleted: boolean;
  completing: boolean;
}

const ModuleViewer: React.FC<ModuleViewerProps> = ({
  module, open, onClose, onComplete, isCompleted, completing,
}) => {
  if (!module) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <School color="primary" />
            <Typography variant="h6" fontWeight={700}>{module.title}</Typography>
          </Box>
          {isCompleted && <Chip label="Abgeschlossen" color="success" size="small" />}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
          {module.description}
        </Typography>

        {/* Main content - split by paragraphs */}
        {module.content.split('\n\n').map((paragraph, i) => (
          <Typography key={i} variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            {paragraph}
          </Typography>
        ))}

        <Divider sx={{ my: 3 }} />

        {/* Key Points */}
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LightbulbOutlined color="warning" /> Wichtigste Punkte
        </Typography>
        <List dense>
          {module.keyPoints.map((point, i) => (
            <ListItem key={i}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircle color="success" sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText primary={point} />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 3 }} />

        {/* Practical Example */}
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          Praxis-Beispiel
        </Typography>
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, borderLeft: 3, borderColor: 'primary.main' }}>
          <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
            {module.example}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Schließen</Button>
        {!isCompleted && (
          <Button
            variant="contained"
            onClick={() => onComplete(module.id)}
            disabled={completing}
          >
            {completing ? 'Wird gespeichert...' : 'Modul abschließen'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ModuleViewer;
